/**
 * RTK Query API Slice
 * Integrates the API client with RTK Query
 */

import {createApi} from '@reduxjs/toolkit/query/react';
import {ApiClient} from '../ApiClient';
import {RequestConfig} from '../types';

/**
 * Custom base query function that uses our ApiClient
 * @param apiClient - The API client instance
 * @returns A base query function for RTK Query
 */
export const createApiClientBaseQuery = (apiClient: ApiClient) => {
  return async ({ url, method, data, params, ...rest }: RequestConfig) => {
    try {
      // Execute the request using our ApiClient
      const result = await apiClient.request({
        url,
        method,
        data,
        params,
        ...rest,
      });
      
      return { data: result.data };
    } catch (error) {
      return {
        error: {
          status: (error as any).status,
          data: (error as any).details,
        },
      };
    }
  };
};

/**
 * Creates an RTK Query API slice using our ApiClient
 * @param apiClient - The API client instance
 * @param baseUrl - The base URL for API endpoints
 * @returns An RTK Query API slice
 */
export const createApiSlice = (apiClient: ApiClient, baseUrl = '') => {
  return createApi({
    reducerPath: 'api',
    baseQuery: createApiClientBaseQuery(apiClient),
    tagTypes: ['User', 'Role', 'Settings'], // Add more tag types as needed
    endpoints: (builder) => ({
      // Define endpoints here or in separate files that extend this slice
      // Example:
      // getUsers: builder.query({
      //   query: () => ({ url: '/users', method: 'GET' }),
      //   providesTags: ['User'],
      // }),
    }),
  });
};

/**
 * Type for extending the API slice with additional endpoints
 */
export type ApiSlice = ReturnType<typeof createApiSlice>;

/**
 * Helper function to create an endpoint builder
 * @param apiSlice - The API slice to extend
 * @returns An endpoint builder function
 */
export const createEndpointBuilder = (apiSlice: ApiSlice) => {
  return <T>(
    endpointName: string,
    config: {
      query: RequestConfig;
      transformResponse?: (response: any) => T;
      providesTags?: string[] | ((result: T) => any);
      invalidatesTags?: string[];
    }
  ) => {
    return apiSlice.injectEndpoints({
      endpoints: (builder) => ({
        [endpointName]: builder.query<T, void>({
          query: () => config.query,
          transformResponse: config.transformResponse,
          providesTags: config.providesTags as any,
        }),
      }),
      overrideExisting: false,
    }).endpoints;
  };
};

/**
 * Helper function to create a mutation endpoint builder
 * @param apiSlice - The API slice to extend
 * @returns A mutation endpoint builder function
 */
export const createMutationEndpointBuilder = (apiSlice: ApiSlice) => {
  return <T, A>(
    endpointName: string,
    config: {
      query: (arg: A) => RequestConfig;
      transformResponse?: (response: any) => T;
      invalidatesTags?: string[] | ((result: T, arg: A) => any);
      onQueryStarted?: (arg: A, api: any) => Promise<void>;
    }
  ) => {
    return apiSlice.injectEndpoints({
      endpoints: (builder) => ({
        [endpointName]: builder.mutation<T, A>({
          query: config.query,
          transformResponse: config.transformResponse,
          invalidatesTags: config.invalidatesTags as any,
          onQueryStarted: config.onQueryStarted,
        }),
      }),
      overrideExisting: false,
    }).endpoints;
  };
};
